import React, {FC} from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory} from 'react-router'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import {Box, Theme} from '@mui/material'
import {PGButton} from '@progynyglobal/pg-ui-library'
import {makeStyles} from 'tss-react/mui'

import SubmitButton from '@progyny/components/Buttons/SubmitButton'

export interface NavigatorProps {
  showBackButton?: boolean
  backButtonType?: 'text' | 'contained' | 'outlined'
  buttonDisable?: boolean
  onPrevButtonClick?: () => Promise<any> | void
  onNextButtonClick?: () => Promise<any> | void
  onSkipButtonClick?: () => Promise<any> | void
  type?: 'button' | 'submit' | 'reset'
  label?: string
  buttonSize?: 'small' | 'medium' | 'large'
  submitDataRole?: string
  skipDataRole?: string
  loading?: boolean
  canSkip?: boolean
  skipButtonLabel?: string
  singleCentered?: boolean
  fullWidth?: boolean
}

const useStyles = makeStyles<{
  singleCentered?: boolean
  fullWidth?: boolean
}>()((theme: Theme, props) => ({
  main: {
    display: 'flex',
    justifyContent: props.singleCentered ? 'center' : 'space-between',
    width: props.fullWidth ? '100%' : undefined,
    gap: theme.spacing(2),
  },
}))

const Navigator: FC<NavigatorProps> = (props) => {
  const history = useHistory()
  const {t} = useTranslation()
  const {
    showBackButton = true,
    backButtonType = 'text',
    buttonDisable = false,
    onPrevButtonClick,
    onNextButtonClick,
    type,
    label,
    submitDataRole,
    skipDataRole,
    loading,
    onSkipButtonClick,
    canSkip,
    skipButtonLabel,
    singleCentered,
    fullWidth,
  } = props

  const {classes} = useStyles({singleCentered, fullWidth})
  const nextLabel = label || t('navigator_next_step')
  const skipLabel = skipButtonLabel || t('navigator_skip')

  const handleClick = async () => {
    onNextButtonClick && (await onNextButtonClick())
  }

  if (onSkipButtonClick && !onNextButtonClick) {
    throw new Error('If we have a skip button we also need a next button.')
  }

  const backButtonMarkup = (
    <PGButton
      data-role={'previous'}
      key={'previous'}
      variant={'outlined'}
      startIcon={backButtonType === 'text' ? <ArrowBackIcon /> : undefined}
      onClick={() => {
        if (onPrevButtonClick) {
          onPrevButtonClick()
        } else {
          history.goBack()
        }
      }}
      size='medium'
      disableRipple
      label={t('common_back')}
    />
  )
  const submitButtonMarkup = (
    <SubmitButton
      data-role={
        submitDataRole ? `${submitDataRole}-submit-button` : 'submit-button'
      }
      key={'action'}
      disabled={buttonDisable}
      onClick={handleClick}
      type={type || undefined}
      size={'large'}
      loading={loading}
      fullWidth={fullWidth}
    >
      {nextLabel}
    </SubmitButton>
  )
  const skipButtonMarkup = (
    <SubmitButton
      data-role={skipDataRole ? `${skipDataRole}-skip-button` : 'skip-button'}
      key={'skip'}
      loading={loading}
      onClick={onSkipButtonClick}
      size={'large'}
    >
      {skipLabel}
    </SubmitButton>
  )

  return (
    <Box className={classes.main}>
      {showBackButton && <>{backButtonMarkup}</>}
      {onSkipButtonClick ? (
        canSkip ? (
          <>{skipButtonMarkup}</>
        ) : (
          <>{submitButtonMarkup}</>
        )
      ) : (
        onNextButtonClick && <>{submitButtonMarkup}</>
      )}
    </Box>
  )
}

export {Navigator}
